"use client";

import React from "react";
import { ChevronDown } from "lucide-react";
import { buttonVariants } from "@workspace/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@workspace/ui/components/dropdown-menu";
import { AppHeader } from "@workspace/ui/components/app-header";
import Link from "next/link";

type LinkItem = {
  label: string;
  href: string;
  description?: string;
};

type SubMenu = {
  label: string;
  children: MenuItem[];
};

type SubMenuDivider = {
  label: string;
  description?: string;
};

export type MenuItem = SubMenuDivider | LinkItem | SubMenu;

function isSubMenu(item: MenuItem): item is SubMenu {
  return "children" in item && Array.isArray(item.children);
}

function isLinkItem(item: MenuItem): item is LinkItem {
  return "href" in item;
}

/**
 * Renders a list of MenuItems as navigation links and dropdown menus.
 * Extracted so sub-apps can reuse the standard menu pattern in their header slot.
 */
export function HeaderNav({ links = [] as MenuItem[] }) {
  return (
    <div className="flex items-center gap-1">
      {links.map((link) =>
        isSubMenu(link) ? (
          <DropdownMenu key={link.label}>
            <DropdownMenuTrigger className="text-sm">
              {link.label} <ChevronDown className="inline h-10" />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {link.children.map((child: MenuItem) =>
                isLinkItem(child) ? (
                  <DropdownMenuItem key={child.href} asChild>
                    <Link href={child.href}>{child.label}</Link>
                  </DropdownMenuItem>
                ) : (
                  <React.Fragment key={child.label}>
                    <DropdownMenuLabel className="uppercase opacity-40 font-semibold text-xs">
                      {child.label}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                  </React.Fragment>
                ),
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : isLinkItem(link) ? (
          <Link
            className={buttonVariants({ variant: "ghost", size: "sm" })}
            href={link.href}
            key={link.label}
          >
            {link.label}
          </Link>
        ) : null,
      )}
    </div>
  );
}

/**
 * Backward-compatible floating header.
 * Wraps AppHeader with variant="floating" and renders links via HeaderNav.
 */
export function FloatingHeader({ links = [] as MenuItem[] }) {
  return (
    <AppHeader variant="floating">
      {links.length > 0 && <HeaderNav links={links} />}
    </AppHeader>
  );
}
